import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AnalogClock = ({ court, selectedDate, timeSlotsData, showCustomerNames = false, showAllTimeSlots = false }) => {
  const { openingHours } = court;
  
  // Set default isPM based on court period type and current time
  const getDefaultIsPM = () => {
    // First, analyze what periods are available for this court
    const analyzeCourtPeriods = () => {
      let hasAMSlots = false;
      let hasPMSlots = false;
      
      // Check AM period (0-11 hours)
      for (let hour = 0; hour < 12; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const apiTimeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const processedSlot = timeSlotsData.find(slot => slot.time === apiTimeString);
          if (processedSlot && (processedSlot.status === 'available' || processedSlot.status === 'booked')) {
            hasAMSlots = true;
            break;
          }
        }
        if (hasAMSlots) break;
      }
      
      // Check PM period (12-23 hours)
      for (let hour = 12; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const apiTimeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const processedSlot = timeSlotsData.find(slot => slot.time === apiTimeString);
          if (processedSlot && (processedSlot.status === 'available' || processedSlot.status === 'booked')) {
            hasPMSlots = true;
            break;
          }
        }
        if (hasPMSlots) break;
      }
      
      return { hasAMSlots, hasPMSlots };
    };
    
    const { hasAMSlots, hasPMSlots } = analyzeCourtPeriods();
    
    // If court is PM-only, default to PM
    if (hasPMSlots && !hasAMSlots) {
      return true;
    }
    
    // If court is AM-only, default to AM
    if (hasAMSlots && !hasPMSlots) {
      return false;
    }
    
    // If court has both periods, use current time logic for today, default to AM for future dates
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    if (isToday) {
      return now.getHours() >= 12; // PM if current hour is 12 or later
    }
    
    return false; // Default to AM for future dates when both periods are available
  };
  
  const [isPM, setIsPM] = useState(getDefaultIsPM());
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hoveredSector, setHoveredSector] = useState(null);
  const { t } = useLanguage();
  
  // Update isPM when timeSlotsData changes (different date selected)
  useEffect(() => {
    setIsPM(getDefaultIsPM());
  }, [timeSlotsData, selectedDate]);
  
  // Analyze court opening hours to determine display type
  const analyzeCourtPeriods = () => {
    let hasAMSlots = false;
    let hasPMSlots = false;
    
    // Check AM period (0-11 hours)
    for (let hour = 0; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const apiTimeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const processedSlot = timeSlotsData.find(slot => slot.time === apiTimeString);
        if (processedSlot && (processedSlot.status === 'available' || processedSlot.status === 'booked')) {
          hasAMSlots = true;
          break;
        }
      }
      if (hasAMSlots) break;
    }
    
    // Check PM period (12-23 hours)
    for (let hour = 12; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const apiTimeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const processedSlot = timeSlotsData.find(slot => slot.time === apiTimeString);
        if (processedSlot && (processedSlot.status === 'available' || processedSlot.status === 'booked')) {
          hasPMSlots = true;
          break;
        }
      }
      if (hasPMSlots) break;
    }
    
    // Determine display type
    if (hasAMSlots && hasPMSlots) {
      return 'both'; // AM+PM - show toggle
    } else if (hasAMSlots) {
      return 'am-only'; // AM only - show single clock with AM tag
    } else if (hasPMSlots) {
      return 'pm-only'; // PM only - show single clock with PM tag
    } else {
      return 'none'; // No slots available
    }
  };
  
  const courtPeriodType = analyzeCourtPeriods();
  
  // Generate all time slots for 12 hours (every 30 minutes)
  const generateTimeSlots = () => {
    const slots = [];
    
    for (let hour = 0; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayHour = hour === 0 ? 12 : hour;
        const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')}`;
        
        // Convert to 24-hour format for API comparison
        const apiHour = isPM ? hour + 12 : hour;
        const apiTimeString = `${apiHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const totalMinutes = apiHour * 60 + minute;
        
        // Find the status from the processed data
        const processedSlot = timeSlotsData.find(slot => slot.time === apiTimeString);
        const status = processedSlot ? processedSlot.status : 'closed';
        const customerBookingName = processedSlot ? processedSlot.customerBookingName : null;
        const customerDisplayName = processedSlot ? processedSlot.customerDisplayName : null;
        
        slots.push({
          time: displayTime,
          apiTime: apiTimeString,
          totalMinutes,
          status,
          customerBookingName,
          customerDisplayName
        });
      }
    }
    return slots;
  };
  
  const timeSlots = generateTimeSlots();
  
  // Calculate angle for 12-hour format
  const getAngle = (totalMinutes) => {
    const hour = Math.floor(totalMinutes / 60) % 12;
    const minute = totalMinutes % 60;
    return ((hour + minute / 60) / 12) * 360;
  };

  // Helper function to get the end time of a time slot (add 30 minutes)
  const getEndTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    let endMinutes = minutes + 30;
    let endHours = hours;
    
    if (endMinutes >= 60) {
      endMinutes = 0;
      endHours += 1;
    }
    
    // Handle 12-hour format conversion
    if (endHours > 12) {
      endHours = endHours - 12;
    } else if (endHours === 0) {
      endHours = 12;
    }
    
    return `${endHours}:${endMinutes.toString().padStart(2, '0')}`;
  };

  // Generate sectors for availability visualization
  const generateSectors = () => {
    const sectors = [];
    let currentStatus = null;
    let currentCustomerBookingName = null;
    let currentCustomerDisplayName = null;
    let startAngle = 0;
    let sectorStartIndex = 0;
    
    timeSlots.forEach((slot, index) => {
      const angle = getAngle(slot.totalMinutes);
      
      if (currentStatus !== slot.status || currentCustomerBookingName !== slot.customerBookingName || currentCustomerDisplayName !== slot.customerDisplayName) {
        // End previous sector
        if (currentStatus !== null) {
          // Calculate the correct start and end times for the sector
          const sectorStartTime = timeSlots[sectorStartIndex].time;
          const sectorEndTime = getEndTime(timeSlots[index - 1].time);
          
          sectors.push({
            status: currentStatus,
            customerBookingName: currentCustomerBookingName,
            customerDisplayName: currentCustomerDisplayName,
            startAngle: startAngle,
            endAngle: angle,
            startTime: sectorStartTime,
            endTime: sectorEndTime
          });
        }
        
        // Start new sector
        currentStatus = slot.status;
        currentCustomerBookingName = slot.customerBookingName;
        currentCustomerDisplayName = slot.customerDisplayName;
        startAngle = angle;
        sectorStartIndex = index;
      }
    });
    
    // Add final sector
    if (currentStatus !== null) {
      const sectorStartTime = timeSlots[sectorStartIndex].time;
      const sectorEndTime = getEndTime(timeSlots[timeSlots.length - 1].time);
      
      sectors.push({
        status: currentStatus,
        customerBookingName: currentCustomerBookingName,
        customerDisplayName: currentCustomerDisplayName,
        startAngle: startAngle,
        endAngle: 360,
        startTime: sectorStartTime,
        endTime: sectorEndTime
      });
    }
    
    return sectors;
  };
  
  // Get color based on status
  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return '#10b981'; // green-500
      case 'booked':
        return '#ef4444'; // red-500
      case 'closed':
        return 'transparent'; // transparent
      default:
        return 'transparent'; // transparent
    }
  };
  
  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return t('available');
      case 'booked':
        return t('booked');
      case 'closed':
        return t('closed');
      default:
        return t('closed');
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 300); // Match slide-down animation duration
  };

  return (
    <>
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={handleClose}
          ></div>
          
          {/* Modal */}
          <div className={`relative w-full max-w-md max-h-[90vh] sm:max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl transform transition-all duration-300 ease-out sm:scale-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`}>
            {/* Handle for mobile */}
            <div className="flex justify-center pt-4 pb-2 sm:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(85vh-80px)]">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 text-center">
                {court.name}
              </h3>
              
              {/* Analog Clock Display */}
              <div className="relative w-64 h-64 mx-auto mb-4">
                {/* Clock face */}
                <div className="w-full h-full rounded-full border-4 border-gray-300 bg-gray-50 relative">
                  {/* Clock center */}
                  <div className="absolute top-1/2 left-1/2 w-5 h-5 bg-gray-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"></div>
                  
                  {/* Hour numbers */}
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((hour) => {
                    const angle = (hour / 12) * 360 - 90;
                    const radian = angle * (Math.PI / 180);
                    const x = Math.cos(radian) * 100;
                    const y = Math.sin(radian) * 100;
                    
                    return (
                      <div
                        key={hour}
                        className="absolute transform -translate-x-1/2 -translate-y-1/2"
                        style={{
                          left: `calc(50% + ${x}px)`,
                          top: `calc(50% + ${y}px)`
                        }}
                      >
                        <span className="text-2xl font-semibold text-black">
                          {hour}
                        </span>
                      </div>
                    );
                  })}
                  
                  {/* Availability sectors */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
                    <defs>
                      <clipPath id={`clockClip-${court.id}`}>
                        <circle cx="100" cy="100" r="90" />
                      </clipPath>
                    </defs>
                    <g clipPath={`url(#clockClip-${court.id})`}>
                      {generateSectors().map((sector, index) => {
                        const startAngle = sector.startAngle - 90; // Start from 12 o'clock
                        const endAngle = sector.endAngle - 90;
                        const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
                        
                        // Calculate path for sector
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = (endAngle * Math.PI) / 180;
                        
                        const x1 = 100 + 90 * Math.cos(startRad);
                        const y1 = 100 + 90 * Math.sin(startRad);
                        const x2 = 100 + 90 * Math.cos(endRad);
                        const y2 = 100 + 90 * Math.sin(endRad);
                        
                        const pathData = [
                          `M 100 100`,
                          `L ${x1} ${y1}`,
                          `A 90 90 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                          'Z'
                        ].join(' ');
                        
                        return (
                          <g key={`${index}-${isPM}`}>
                            <path
                              d={pathData}
                              fill={getStatusColor(sector.status)}
                              opacity="0.6"
                              className="hover:opacity-80 cursor-pointer"
                              stroke={showCustomerNames && sector.status === 'booked' ? '#000000' : 'none'}
                              strokeWidth={showCustomerNames && sector.status === 'booked' ? '2' : '0'}
                              onMouseEnter={() => setHoveredSector(sector)}
                              onMouseLeave={() => setHoveredSector(null)}
                            />
                            {/* Customer name text for booked slots */}
                            {showCustomerNames && sector.status === 'booked' && sector.customerDisplayName && (
                              <text
                                x={100 + 45 * Math.cos(((sector.startAngle + sector.endAngle) / 2 - 90) * Math.PI / 180)}
                                y={100 + 45 * Math.sin(((sector.startAngle + sector.endAngle) / 2 - 90) * Math.PI / 180)}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-xl font-medium fill-white"
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                  pointerEvents: 'none'
                                }}
                              >
                                {sector.customerDisplayName.length > 15 
                                  ? sector.customerDisplayName.substring(0, 15) + '...' 
                                  : sector.customerDisplayName
                                }
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  </svg>
                </div>
              </div>
              
              {/* Hover Tooltip */}
              {hoveredSector && (hoveredSector.customerDisplayName || hoveredSector.customerBookingName) && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-90 text-white px-4 py-2 rounded-lg text-sm font-medium z-20 pointer-events-none">
                  <div className="text-center">
                    {hoveredSector.customerDisplayName && (
                      <div className="font-bold text-lg">{hoveredSector.customerDisplayName}</div>
                    )}
                    {hoveredSector.customerBookingName && (
                      <div className="text-sm opacity-90">
                        ชื่อที่จอง: {hoveredSector.customerBookingName}
                      </div>
                    )}
                    <div className="text-xs opacity-80">
                      {hoveredSector.startTime} - {hoveredSector.endTime} {isPM ? 'PM' : 'AM'}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-6 text-lg sm:text-xl mb-6">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-green-500 rounded-full mr-4"></div>
                  <span className="text-gray-700 font-semibold">{t('available')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-red-500 rounded-full mr-4"></div>
                  <span className="text-gray-700 font-semibold">{t('booked')}</span>
                </div>
              </div>
              
              {/* Opening hours */}
              <div className="text-center mb-6 text-lg sm:text-xl text-gray-700">
                <p className="font-semibold">{t('opening_hours')}: <span className="font-bold text-xl">{openingHours.open} - {openingHours.close}</span></p>
              </div>
              
              {/* Period Display - Smart Logic */}
              {courtPeriodType === 'both' && (
                <div className="flex justify-center mb-6">
                  <div 
                    className="bg-gray-100 rounded-full p-1 flex cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPM(!isPM);
                    }}
                  >
                    <div className={`rounded-full font-medium transition-all duration-200 ${
                      !isPM 
                        ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white shadow-sm transform scale-105' 
                        : 'bg-blue-50 text-blue-600'
                    } px-6 py-3 text-base`}>
                      {t('morning')}
                    </div>
                    <div className={`rounded-full font-medium transition-all duration-200 ${
                      isPM 
                        ? 'bg-gradient-to-r from-orange-400 to-red-500 text-white shadow-sm transform scale-105' 
                        : 'bg-orange-50 text-orange-600'
                    } px-6 py-3 text-base`}>
                      {t('afternoon')}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Single Period Tags */}
              {courtPeriodType === 'am-only' && (
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-6 py-3 rounded-full text-base font-medium">
                    {t('morning')}
                  </div>
                </div>
              )}
              
              {courtPeriodType === 'pm-only' && (
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-6 py-3 rounded-full text-base font-medium">
                    {t('afternoon')}
                  </div>
                </div>
              )}
              
              {courtPeriodType === 'none' && (
                <div className="flex justify-center mb-6">
                  <div className="bg-gray-400 text-white px-6 py-3 rounded-full text-base font-medium">
                    No Available Times
                  </div>
                </div>
              )}
              
              {/* Close Button */}
              <div className="flex justify-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="px-12 py-4 bg-red-500 text-white rounded-2xl font-semibold text-lg hover:bg-red-600 transition-colors shadow-lg"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className={`bg-white rounded-2xl shadow-lg transition-all duration-300 ${
        isExpanded ? 'hidden' : 'p-4 sm:p-6 cursor-pointer hover:shadow-xl'
      }`} onClick={() => !isExpanded && setIsExpanded(true)}>
        <h4 className="font-semibold text-gray-800 text-center text-lg sm:text-xl mb-3 sm:mb-4">
          {court.name}
        </h4>
        
        {/* Analog Clock Display */}
        <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto mb-3 sm:mb-4">
          {/* Clock face */}
          <div className="w-full h-full rounded-full border-4 border-gray-300 bg-gray-50 relative">
            {/* Clock center */}
            <div className="absolute top-1/2 left-1/2 w-3 h-3 sm:w-4 sm:h-4 bg-gray-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"></div>
            
            {/* Hour numbers */}
            {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((hour) => {
              const angle = (hour / 12) * 360 - 90;
              const radian = angle * (Math.PI / 180);
              const x = Math.cos(radian) * 60;
              const y = Math.sin(radian) * 60;
              
              return (
                <div
                  key={hour}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: `calc(50% + ${x}px)`,
                    top: `calc(50% + ${y}px)`
                  }}
                >
                  <span className="text-base font-semibold text-black">
                    {hour}
                  </span>
                </div>
              );
            })}
            
            {/* Availability sectors */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
              <defs>
                <clipPath id={`clockClip-${court.id}`}>
                  <circle cx="100" cy="100" r="90" />
                </clipPath>
              </defs>
              <g clipPath={`url(#clockClip-${court.id})`}>
                {generateSectors().map((sector, index) => {
                  const startAngle = sector.startAngle - 90; // Start from 12 o'clock
                  const endAngle = sector.endAngle - 90;
                  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
                  
                  // Calculate path for sector
                  const startRad = (startAngle * Math.PI) / 180;
                  const endRad = (endAngle * Math.PI) / 180;
                  
                  const x1 = 100 + 90 * Math.cos(startRad);
                  const y1 = 100 + 90 * Math.sin(startRad);
                  const x2 = 100 + 90 * Math.cos(endRad);
                  const y2 = 100 + 90 * Math.sin(endRad);
                  
                  const pathData = [
                    `M 100 100`,
                    `L ${x1} ${y1}`,
                    `A 90 90 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    'Z'
                  ].join(' ');
                  
                  return (
                    <g key={`${index}-${isPM}`}>
                      <path
                        d={pathData}
                        fill={getStatusColor(sector.status)}
                        opacity="0.6"
                        className="hover:opacity-80 cursor-pointer"
                        stroke={showCustomerNames && sector.status === 'booked' ? '#000000' : 'none'}
                        strokeWidth={showCustomerNames && sector.status === 'booked' ? '2' : '0'}
                        onMouseEnter={() => setHoveredSector(sector)}
                        onMouseLeave={() => setHoveredSector(null)}
                      />
                      {/* Customer name text for booked slots */}
                      {showCustomerNames && sector.status === 'booked' && sector.customerDisplayName && (
                        <text
                          x={100 + 45 * Math.cos(((sector.startAngle + sector.endAngle) / 2 - 90) * Math.PI / 180)}
                          y={100 + 45 * Math.sin(((sector.startAngle + sector.endAngle) / 2 - 90) * Math.PI / 180)}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xl font-medium fill-white"
                          style={{
                            fontSize: '8px',
                            fontWeight: 'bold',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                            pointerEvents: 'none'
                          }}
                        >
                          {sector.customerDisplayName.length > 15 
                            ? sector.customerDisplayName.substring(0, 15) + '...' 
                            : sector.customerDisplayName
                          }
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>
        </div>
        
        {/* Hover Tooltip for collapsed view */}
        {hoveredSector && (hoveredSector.customerDisplayName || hoveredSector.customerBookingName) && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-90 text-white px-3 py-1 rounded-lg text-xs font-medium z-20 pointer-events-none">
            <div className="text-center">
              {hoveredSector.customerDisplayName && (
                <div className="font-bold text-sm">{hoveredSector.customerDisplayName}</div>
              )}
              {hoveredSector.customerBookingName && (
                <div className="text-xs opacity-90">
                  ชื่อที่จอง: {hoveredSector.customerBookingName}
                </div>
              )}
              <div className="text-xs opacity-80">
                {hoveredSector.startTime} - {hoveredSector.endTime} {isPM ? 'PM' : 'AM'}
              </div>
            </div>
          </div>
        )}
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-sm sm:text-base">
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full mr-2 sm:mr-3"></div>
            <span className="text-gray-700 font-medium">{t('available')}</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded-full mr-2 sm:mr-3"></div>
            <span className="text-gray-700 font-medium">{t('booked')}</span>
          </div>
        </div>
        
        {/* Opening hours */}
        <div className="text-center mt-2 sm:mt-3 text-sm sm:text-base text-gray-700">
          <p className="font-medium">{t('opening_hours')}: <span className="font-semibold">{openingHours.open} - {openingHours.close}</span></p>
        </div>
        
        {/* Period Display - Smart Logic */}
        {courtPeriodType === 'both' && (
          <div className="flex justify-center mt-3 sm:mt-4">
            <div 
              className="bg-gray-100 rounded-full p-1 flex cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setIsPM(!isPM);
              }}
            >
              <div className={`rounded-full font-medium transition-all duration-200 ${
                !isPM 
                  ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white shadow-sm transform scale-105' 
                  : 'bg-blue-50 text-blue-600'
              } px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm`}>
                {t('morning')}
              </div>
              <div className={`rounded-full font-medium transition-all duration-200 ${
                isPM 
                  ? 'bg-gradient-to-r from-orange-400 to-red-500 text-white shadow-sm transform scale-105' 
                  : 'bg-orange-50 text-orange-600'
              } px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm`}>
                {t('afternoon')}
              </div>
            </div>
          </div>
        )}
        
        {/* Single Period Tags */}
        {courtPeriodType === 'am-only' && (
          <div className="flex justify-center mt-3 sm:mt-4">
            <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              {t('morning')}
            </div>
          </div>
        )}
        
        {courtPeriodType === 'pm-only' && (
          <div className="flex justify-center mt-3 sm:mt-4">
            <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              {t('afternoon')}
            </div>
          </div>
        )}
        
        {courtPeriodType === 'none' && (
          <div className="flex justify-center mt-3 sm:mt-4">
            <div className="bg-gray-400 text-white px-4 py-2 rounded-full text-sm font-medium">
              No Available Times
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AnalogClock; 